import { initProjectConfiguration } from "@quatico/magellan-client";

import { iamFetchTransport } from "./iam-transport";

/**
 * IAM-CDS integration, isolated so it can be lifted into a dedicated
 * `@kmu-trexpert/iam` package later. The host calls only the exports here; all
 * IAM-CDS specifics (the second micro-frontend, its Magellan namespaces, its
 * transport, the login touchpoint) stay behind this boundary.
 *
 * Enabled by the runtime config injected into the page
 * (`window.__TREXPERT_CONFIG__.enableIam`, set by HtmlWebpackPlugin from the
 * server/build env). With it off, IAM-CDS is absent and the host falls back to
 * the in-memory dev-persona login — the dual-mode principle (CLAUDE.md rule 6).
 * Reading at runtime (not a build-time constant) means the same bundle works in
 * any environment and a deployment can flip the flag without rebuilding.
 */

declare global {
    interface Window {
        __TREXPERT_CONFIG__?: { enableIam?: boolean };
        /** Theme contract read by the IAM-CDS touchpoints (mirrors @qs/cds-iam-client's HostTheme). */
        __IAM_THEME__?: { shadowStylesheets?: string[] };
    }
}

export const IAM_FRONTEND = "iam-cds";

/**
 * Re-skin the embedded IAM-CDS touchpoints to match the trexpert design system.
 * Two tiers (see iam-theme.css / iam-theme-shadow.css):
 *   - Tier 1 (tokens): a <link> whose `qs-touchpoint-element > *` rule sets the
 *     IAM shadcn tokens directly on each touchpoint's shadow host.
 *   - Tier 2 (rules): `window.__IAM_THEME__.shadowStylesheets`, fetched by each
 *     touchpoint and adopted into its shadow root (table/card polish).
 *
 * Applied UNCONDITIONALLY (not gated on `isIamCdsEnabled`): it is inert when no
 * IAM touchpoints are mounted, and avoids a flag dependency. Must run before the
 * touchpoints mount so the first paint is themed. Idempotent.
 */
export const applyIamTheme = (): void => {
    if (typeof document === "undefined") {
        return;
    }
    const LINK_ID = "iam-host-theme";
    if (!document.getElementById(LINK_ID)) {
        const link = document.createElement("link");
        link.id = LINK_ID;
        link.rel = "stylesheet";
        link.href = "/iam-theme.css";
        document.head.appendChild(link);
    }
    window.__IAM_THEME__ = { shadowStylesheets: ["/iam-theme-shadow.css"] };
};

// IAM-CDS Magellan contract (mirrors @qs/cds-iam-shared). Relative endpoints
// resolve against the page origin; the dev server proxies /cds/iam → IAM_HOST.
const IAM_NO_AUTH_NAMESPACE = "iam-no-auth";
const IAM_AUTH_NAMESPACE = "iam-auth";
const IAM_NO_AUTH_ENDPOINT = "/cds/iam/api/no-auth";
const IAM_AUTH_ENDPOINT = "/cds/iam/api/auth";
const IAM_TRANSPORT_NAME = "iam-fetch";

/** The IAM auth touchpoints this host mounts (served by the IAM-CDS MFE). */
export const IAM_TOUCHPOINTS = {
    /** Session control in the app chrome: shows Login when out, user + logout when in. */
    loginButton: "login-button",
    /** The full login form, mounted on the dedicated login page. */
    login: "login-view",
    register: "onboarding-view",
    forgotPassword: "forgot-password-view",
    passwordReset: "password-reset-view",
    // IAM-CDS administration surfaces, shown in the nav when delegation is on.
    userManagement: "user-management",
    organizationManagement: "organization-management",
    templateManagement: "organization-template-management",
    applicationManagement: "application-management",
    identityProviderManagement: "identity-provider-management",
    /** Account self-service (the «Profil» nav entry in IAM mode). */
    profile: "profile-view",
} as const;

/** Dedicated login surface — `login-button` autoRedirects here when signed out. */
export const IAM_LOGIN_PATH = "/login.html";

/**
 * The IAM app id both the login-button and login-view authenticate against. They
 * MUST share it: the UserManager session is keyed by `iam-auth:<iamAppId>:<appId>`,
 * so a mismatch makes one surface think you're logged out while the other thinks
 * you're logged in — an endless redirect between the app and the login page.
 */
export const IAM_APP_ID = "iam";
export const TREXPERT_PORTAL_APP_ID = "trexpert-portal";
/** True when IAM-CDS delegation is on (vs in-memory persona login). */
export const isIamCdsEnabled = (): boolean =>
    typeof window !== "undefined" && window.__TREXPERT_CONFIG__?.enableIam === true;

/**
 * The IAM-CDS micro-frontend registration, or `null` when disabled. Spread into
 * the app-shell's `microFrontends` (filtering nulls). The MFE is served at
 * `${origin}/cds/iam/ui/mfe.js`; the dev server proxies /cds/iam to IAM-CDS.
 */
export const iamMicroFrontend = (): { name: string; originUrl: string; useTouchpoints: "*" } | null => {
    if (!isIamCdsEnabled()) {
        return null;
    }
    const origin = window.location.origin;
    return { name: IAM_FRONTEND, originUrl: `${origin}/cds/iam/ui/mfe.js`, useTouchpoints: "*" };
};

/**
 * Register the IAM-CDS Magellan namespaces + transport. Call inside the app-shell
 * `beforeAll`, alongside the trexpert transport config. No-op when disabled.
 */
export const configureIamTransport = (): void => {
    if (!isIamCdsEnabled()) {
        return;
    }
    // MERGE — not replace. `initProjectConfiguration` overwrites the global
    // config unless `mergeWithExisting` is set, so without it this call would
    // wipe the trexpert namespaces registered just before (`configureTransport`),
    // leaving every trexpert call (e.g. getDashboard) with no transport — it then
    // fails silently with no network request. Merging keeps both side by side.
    initProjectConfiguration(
        {
            namespaces: {
                [IAM_NO_AUTH_NAMESPACE]: { endpoint: IAM_NO_AUTH_ENDPOINT, transport: IAM_TRANSPORT_NAME },
                [IAM_AUTH_NAMESPACE]: { endpoint: IAM_AUTH_ENDPOINT, transport: IAM_TRANSPORT_NAME },
            },
            transports: { [IAM_TRANSPORT_NAME]: iamFetchTransport },
        },
        { mergeWithExisting: true }
    );
};
